import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import api, { timeAgo } from "@/lib/api";
import { Article } from "@/lib/types";
import { CoinIcon, Pct, fmtPrice } from "@/components/layernav/primitives";
import { COIN_META } from "@/components/layernav/navConfig";
import { detectAssets } from "@/components/article/MarketContext";
import { AreaChart } from "./AreaChart";

const fmtBig = (n?: number | null) => {
  if (!n) return null;
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(0)}M`;
  return `$${Math.round(n).toLocaleString()}`;
};

const fmtSupply = (n?: number | null, sym?: string) => {
  if (!n) return null;
  const v = n >= 1e9 ? `${(n / 1e9).toFixed(1)}B` : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : Math.round(n).toLocaleString();
  return `${v} ${sym || ""}`.trim();
};

const Stat = ({ k, v, testid }: { k: string; v: string; testid: string }) => (
  <div data-testid={testid} className="min-w-0">
    <p className="font-mono text-[9px] uppercase tracking-wider text-gray-500">{k}</p>
    <p className="font-mono text-[13px] text-white mt-0.5 truncate">{v}</p>
  </div>
);

const CoinTab = ({ sym }: { sym: string }) => {
  const { data } = useQuery({
    queryKey: ["asset-chart", sym, "24h"],
    queryFn: async () => (await api.get(`/markets/${sym}?range=24h`)).data,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  if (!data) return <p className="py-6 text-center font-mono text-[10px] text-gray-600">Loading market data…</p>;
  const points = (data.chart || []) as { t: number; price: number }[];
  const up = (data.change_24h ?? 0) >= 0;
  const stats = [
    { k: "Market Cap", v: fmtBig(data.market_cap_usd), t: "mc-stat-mcap" },
    { k: "Circulating Supply", v: fmtSupply(data.circulating_supply, sym), t: "mc-stat-supply" },
    { k: "24H Volume", v: fmtBig(data.volume_24h_usd_global), t: "mc-stat-volume" },
    { k: "24H High", v: data.high_24h ? fmtPrice(data.high_24h) : null, t: "mc-stat-high" },
  ].filter((s) => s.v);
  return (
    <>
      <div className="flex items-center gap-2.5" data-testid="mc-coin-identity">
        <CoinIcon symbol={sym} size={26} />
        <p className="text-[13.5px] font-bold text-white">{data.name || COIN_META[sym]?.name || sym}</p>
        <p className="font-mono text-[10px] uppercase text-gray-600">{sym}</p>
      </div>
      <div className="mt-2.5 flex items-baseline gap-2.5">
        <p className="font-mono text-[23px] text-white leading-none" data-testid="mc-price">{fmtPrice(data.price)}</p>
        <p className="font-mono text-[12px]"><Pct v={data.change_24h} /> <span className="text-gray-600 text-[10px]">(24H)</span></p>
      </div>
      {points.length > 1 && (
        <div className="mt-3.5">
          <AreaChart points={points} up={up} height={92} xMode="hour" testid="mc-chart"
            ariaLabel={`${data.name || sym} 24-hour price chart. Current price ${fmtPrice(data.price)}, ${
              up ? "up" : "down"} ${Math.abs(data.change_24h ?? 0).toFixed(2)} percent over 24 hours.`} />
        </div>
      )}
      {stats.length > 0 && (
        <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-white/[0.06] pt-3.5">
          {stats.map((s) => <Stat key={s.k} k={s.k} v={s.v!} testid={s.t} />)}
        </div>
      )}
      <Link to={`/coin/${sym}`} data-testid="mc-market-page-link"
        className="mt-4 pt-3 border-t border-white/[0.06] flex items-center gap-1.5 font-mono text-[10.5px] font-bold uppercase tracking-widest text-[#00E5FF] hover:text-white transition-colors">
        View {data.name || sym} Market Page <ArrowRight className="h-3 w-3" />
      </Link>
    </>
  );
};

const TotalTab = () => {
  const { data: g } = useQuery({
    queryKey: ["markets-global"],
    queryFn: async () => (await api.get("/markets/global")).data,
    staleTime: 10 * 60 * 1000,
  });
  if (!g?.available) return <p className="py-6 text-center font-mono text-[10px] text-gray-600">Loading market data…</p>;
  const rows = [
    { k: "Total Market Cap", v: fmtBig(g.total_market_cap_usd), pct: g.market_cap_change_24h_pct },
    { k: "24H Volume", v: fmtBig(g.volume_24h_usd) },
    { k: "BTC Dominance", v: typeof g.btc_dominance === "number" ? `${g.btc_dominance.toFixed(1)}%` : null },
    { k: "ETH Dominance", v: typeof g.eth_dominance === "number" ? `${g.eth_dominance.toFixed(1)}%` : null },
  ].filter((r) => r.v);
  return (
    <div data-testid="mc-total-tab">
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.k} className="flex items-center justify-between gap-3">
            <span className="font-mono text-[9px] uppercase tracking-wider text-gray-500">{r.k}</span>
            <span className="flex items-center gap-2 font-mono text-[13px] text-white">
              {r.v}
              {typeof r.pct === "number" && <Pct v={r.pct} className="text-[10px]" />}
            </span>
          </div>
        ))}
      </div>
      {g.updated_at && (
        <p className="mt-3 pt-2.5 border-t border-white/[0.06] font-mono text-[9px] uppercase tracking-wider text-gray-600">
          Updated {timeAgo(g.updated_at)}
        </p>
      )}
      <Link to="/markets" data-testid="mc-markets-link"
        className="mt-3.5 flex items-center gap-1.5 font-mono text-[10.5px] font-bold uppercase tracking-widest text-[#00E5FF] hover:text-white transition-colors">
        View All Markets <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );
};

const DEFAULT_TABS = ["ETH", "BTC", "SOL"];

/* Right-rail module 1 (§22-23): context-aware market panel.
   Non-market stories render nothing — no forced token widgets. */
export const MarketContextPanel = ({ article }: { article: Article }) => {
  const ctx: any = article.context;
  const family = ctx?.family || "GENERAL";
  const primary = ctx?.asset || ctx?.assets_detected?.[0] || detectAssets(article)[0] || null;
  const isGlobal = family === "GLOBAL_MARKET";
  const [active, setActive] = useState<string>(isGlobal && !primary ? "TOTAL" : primary || "TOTAL");

  if (!primary && !isGlobal) return null;

  const coinTabs = primary
    ? [primary, ...DEFAULT_TABS.filter((s) => s !== primary && COIN_META[s])].slice(0, 3)
    : DEFAULT_TABS;
  const tabs = [...coinTabs, "TOTAL"];

  return (
    <section data-testid="market-context-panel" data-track="context_market"
      className="rounded-xl border border-white/[0.08] bg-[#0a0b10] p-4">
      <p className="font-mono text-[9px] font-bold uppercase tracking-[0.28em] text-[#00E5FF]">Market Context</p>
      <div className="mt-3 flex items-center gap-1 border-b border-white/[0.07]" role="tablist" aria-label="Market context asset">
        {tabs.map((tab) => (
          <button key={tab} type="button" role="tab" aria-selected={active === tab}
            onClick={() => setActive(tab)} data-testid={`mc-tab-${tab.toLowerCase()}`}
            className={`px-2.5 pb-2 font-mono text-[10.5px] font-bold uppercase tracking-wider transition-colors border-b-2 -mb-px ${
              active === tab ? "text-white border-[#7C3AED]" : "text-gray-500 border-transparent hover:text-gray-300"}`}>
            {tab}
          </button>
        ))}
      </div>
      <div className="mt-3.5">
        {active === "TOTAL" ? <TotalTab /> : <CoinTab sym={active} />}
      </div>
    </section>
  );
};
